// GPT models that use web search write inline citation markers into their
// reply text: a private-use start char, the marker kind, separator-delimited
// references and a private-use end char, e.g. "\uE200cite\uE202turn3search4\uE202turn3search2\uE201".
// They are rendering instructions for ChatGPT's own UI; we strip them at display
// time only, so the stored reply keeps them for future source links.

const START = "\uE200";
const END = "\uE201";
const SEP = "\uE202";
const PRIVATE_USE = /[\uE200-\uE202]/g;
const CJK = /[\u3000-\u303f\u3400-\u9fff\uff00-\uffef]/;

// Kinds whose whole span is removed. Anything else only loses the invisible chars,
// so an unknown kind can never swallow reply text.
const REMOVED_KINDS = new Set(["cite", "filecite", "navlist", "image_group", "video_group"]);

// While streaming, text after an unclosed start char is held back until the end
// char arrives, but never more than this many chars: past it the marker is treated
// as broken and the text is released.
export const CITATION_HOLD_LIMIT = 200;

export interface StripCitationOptions {
  streaming?: boolean;
}

export function stripCitationMarkers(text: string, options: StripCitationOptions = {}): string {
  if (!/[\uE200-\uE202]/.test(text)) return text;
  let out = "";
  let i = 0;
  while (i < text.length) {
    const start = text.indexOf(START, i);
    if (start < 0) {
      out += text.slice(i).replace(PRIVATE_USE, "");
      break;
    }
    out += text.slice(i, start).replace(PRIVATE_USE, "");
    const end = text.indexOf(END, start + 1);
    const nextStart = text.indexOf(START, start + 1);
    const closed = end >= 0 && (nextStart < 0 || end < nextStart);
    if (!closed) {
      const tail = text.slice(start + 1);
      if (options.streaming && end < 0 && nextStart < 0 && tail.length <= CITATION_HOLD_LIMIT) {
        // Hold the possible marker back until it closes (or proves broken).
        return out;
      }
      // Truncated marker: drop only the invisible chars, keep the text.
      i = start + 1;
      continue;
    }
    const inner = text.slice(start + 1, end);
    const replacement = markerReplacement(inner);
    const rest = text.slice(end + 1);
    out = joinAround(out, replacement, rest);
    i = end + 1;
  }
  return out;
}

// Text files we hand the user (e.g. the research report download) must match what
// the page shows. The stored original is untouched.
export async function stripCitationMarkersFromBlob(blob: Blob): Promise<Blob> {
  const text = await blob.text();
  const stripped = stripCitationMarkers(text);
  return stripped === text ? blob : new Blob([stripped], { type: blob.type });
}

function markerReplacement(inner: string): string {
  const parts = inner.split(SEP);
  const kind = parts[0]?.trim() ?? "";
  if (REMOVED_KINDS.has(kind)) return "";
  if (kind === "entity") {
    // ["people","Display name",...] → show the display name.
    try {
      const value = JSON.parse(parts.slice(1).join(""));
      if (Array.isArray(value) && typeof value[1] === "string") return value[1];
    } catch {
      /* fall through */
    }
  }
  return inner.replace(PRIVATE_USE, "");
}

// Removing a marker must neither leave a double space nor glue two words together.
function joinAround(before: string, replacement: string, rest: string): string {
  if (replacement) return before + replacement;
  const prev = before.at(-1) ?? "";
  const next = rest.charAt(0);
  const prevSpace = prev === " " || prev === "\t";
  const nextBreak = next === "" || /[\s.,;:!?)\]}，。；：！？、）】」』]/.test(next) || next === START;
  if (prevSpace) {
    const trimmed = before.replace(/[ \t]+$/, "");
    // The space was only there to set the marker apart: drop it before a break,
    // and after CJK text, which does not separate words with spaces.
    if (nextBreak || CJK.test(trimmed.at(-1) ?? "")) return trimmed;
  }
  if (prev && !prevSpace && /[A-Za-z0-9]/.test(prev) && /[A-Za-z0-9]/.test(next)) return `${before} `;
  return before;
}
